import { CommonModule } from '@angular/common';
import {
    Component,
    ContentChild,
    HostListener,
    OnDestroy,
    OnInit,
    TemplateRef,
    computed,
    effect,
    inject,
    input,
    output,
    signal
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { SubscriptionStateService } from 'src/app/@core/services/subscription-state.service';
import { TextCasePipe } from 'src/app/@shared/pipes/textCase.pipe';
import { TopBarComponent } from 'src/app/app-layout/components/top-bar/top-bar.component';
import { SidebarViewService } from 'src/app/app-layout/services/sidebar-view.service';
import { SidebarService } from 'src/app/app-layout/services/sidebar.service';
import { ISideBarMenu } from '../../../app-layout/models/sidebar.model';

@Component({
    selector: 'app-side-bar',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        IconComponent,
        ButtonComponent,
        MenuComponent,
        TextCasePipe,
        AvatarModule,
        MenuModule,
        TooltipModule,
        TopBarComponent
    ],
    templateUrl: './side-bar.component.html',
    styleUrl: './side-bar.component.scss'
})
export class SideBarComponent implements OnInit, OnDestroy {

    private readonly subscriptionState = inject(SubscriptionStateService);
    private readonly router = inject(Router);
    readonly sidebarService = inject(SidebarService);
    readonly sidebarViewService = inject(SidebarViewService);

    sidebarId = input<string>('app-sidebar-1');
    showTopBar = input<boolean>(true);
    fullHeight = input<boolean>(true);

    menuItemsScroll = output<Event>();

    @ContentChild('sidebarHeader') sidebarHeader?: TemplateRef<any>;
    @ContentChild('sidebarFooter') sidebarFooter?: TemplateRef<any>;

    private readonly lgBreakpoint = 1024;

    readonly isLocked = computed(() => this.subscriptionState.isSidebarLocked());

    readonly currentView = this.sidebarViewService.currentView;
    readonly isSubView = this.sidebarViewService.isSubView;

    readonly currentMenu = computed<ISideBarMenu[]>(() => this.currentView()?.menu ?? []);
    readonly showFooter = computed<boolean>(() => this.currentView()?.showFooter ?? true);

    /** Per-menu collapsed state (keyed by header string) */
    collapsedStates = new Map<string, boolean>();

    /** Converted options map: menuHeader -> itemIndex -> CustomMenuItem[] */
    convertedMenuItemsMap = new Map<string, Map<number, CustomMenuItem[]>>();

    /** Tracks which sidebar item row is hovered (menuHeader-itemIdx) for conditional options button */
    hoveredItemKey = signal<string | null>(null);

    /** Tracks which item has its options menu open (keep button visible while menu is open) */
    menuOpenForKey = signal<string | null>(null);

    constructor() {
        // Re-initialise collapsed states and converted items whenever the menu changes
        effect(() => {
            const menu = this.currentMenu();
            if (menu && menu.length > 0) {
                this.initCollapsedStates(menu);
                this.rebuildConvertedItems(menu);
            }
        });
    }

    ngOnInit(): void {
        this.sidebarService.registerSidebar(this.sidebarId());
        if (this.fullHeight()) {
            this.handleScreenResize();
        }
    }

    ngOnDestroy(): void { }

    @HostListener('window:resize')
    onResize(): void {
        if (this.fullHeight()) {
            this.handleScreenResize();
        }
    }

    private handleScreenResize(): void {
        const visible = window.innerWidth >= this.lgBreakpoint;
        this.sidebarService.setSidebarVisible(this.sidebarId(), visible);
    }

    // ── Collapsible helpers ───────────────────────────────────────────────

    isMenuCollapsible(menu: ISideBarMenu): boolean {
        return menu.isCollapsible !== false && menu.items !== undefined;
    }

    isMenuCollapsed(header: string): boolean {
        return this.collapsedStates.get(header) ?? false;
    }

    toggleMenu(header: string): void {
        this.collapsedStates.set(header, !this.isMenuCollapsed(header));
    }

    private initCollapsedStates(menu: ISideBarMenu[]): void {
        menu.forEach(item => {
            if (item.items !== undefined && item.isCollapsible !== false) {
                if (!this.collapsedStates.has(item.header)) {
                    this.collapsedStates.set(item.header, item.collapsed ?? false);
                }
            }
        });
    }

    // ── Options dropdown helpers ─────────────────────────────────────────

    getConvertedMenuItems(menuHeader: string, itemIndex: number): CustomMenuItem[] | undefined {
        return this.convertedMenuItemsMap.get(menuHeader)?.get(itemIndex);
    }

    setHoveredItem(key: string | null): void {
        this.hoveredItemKey.set(key);
    }

    isItemHovered(menuHeader: string, itemIdx: number): boolean {
        return this.hoveredItemKey() === `${menuHeader}-${itemIdx}`;
    }

    shouldShowOptionsButton(menuHeader: string, itemIdx: number): boolean {
        const key = `${menuHeader}-${itemIdx}`;
        return this.hoveredItemKey() === key || this.menuOpenForKey() === key;
    }

    onMenuVisibilityChange(visible: boolean, menuHeader: string, itemIdx: number): void {
        const key = `${menuHeader}-${itemIdx}`;
        this.menuOpenForKey.set(visible ? key : null);
    }

    private rebuildConvertedItems(menu: ISideBarMenu[]): void {
        this.convertedMenuItemsMap.clear();
        menu.forEach(section => {
            if (!section.items?.length) return;
            const itemsMap = new Map<number, CustomMenuItem[]>();
            section.items.forEach((item, idx) => {
                if (item.options?.length) {
                    itemsMap.set(idx, item.options.map(opt => ({
                        label: opt.label,
                        icon: opt.icon,
                        iconSource: opt.iconSource === 'primeng' ? IconSourceEnum.PRIMENG
                            : opt.iconSource === 'lucide' ? IconSourceEnum.LUCIDE
                                : IconSourceEnum.SVG,
                        class: opt.class ?? opt.styleClass,
                        callback: () => opt.click(),
                    } as CustomMenuItem)));
                }
            });
            if (itemsMap.size) {
                this.convertedMenuItemsMap.set(section.header, itemsMap);
            }
        });
    }

    // ── Back navigation ──────────────────────────────────────────────────

    navigateBack(): void {
        const view = this.currentView();
        if (view?.backRoute) {
            this.router.navigate([view.backRoute]);
        }
    }

    // ── Scroll ───────────────────────────────────────────────────────────

    onMenuItemsScroll(event: Event): void {
        const view = this.currentView();
        view?.onMenuScroll?.(event);
        this.menuItemsScroll.emit(event);
    }
}
